import React, { useEffect, useMemo, useState } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { itemsPerPage } from '#config';
import { ITabItem } from '#components/navigation/tab-navigator/TabNavigator';
import { useGetSearchResultsWithFacetsQuery } from '#types/generated/apollo-client';
import {
  ContentType,
  Facet,
  SearchParams,
  Teaser,
} from '#types/generated/graphql-types';
import PageableGrid from '#components/grid-block/PageableGrid';
import { VIDEO_FORMAT_360 } from '#config/constants';
import Facets from '#modules/facets/Facets';
import { formatSearchParams, SearchFiltersForVMS } from '#types/filters';

type Props = {
  needle: string;
  filters?: SearchFiltersForVMS;
};

interface SearchOption extends ITabItem {
  prop: string;
  value: string | number | string[];
  count?: number;
}

const SearchGridBlock = ({ needle, filters }: Props) => {
  const { t } = useTranslation();

  const searchOptions: SearchOption[] = useMemo(
    () => [
      { id: 1, title: t('All'), prop: 'contentType', value: 'all' },
      {
        id: 2,
        title: t('gridTabVideos'),
        prop: 'contentType',
        value: ContentType.Video,
      },
      {
        id: 3,
        title: t('gridTabSeries'),
        prop: 'contentType',
        value: ContentType.Series,
      },
      {
        id: 4,
        title: t('gridTabAudios'),
        prop: 'contentType',
        value: ContentType.Audio,
      },
      {
        id: 6,
        title: t('gridTab360Videos'),
        prop: 'tags',
        value: [VIDEO_FORMAT_360],
      },
      {
        id: 5,
        title: t('gridTabLivestreams'),
        prop: 'contentType',
        value: ContentType.LiveStream,
      },
      {
        id: 7,
        title: t('gridTabStudios'),
        prop: 'contentType',
        value: ContentType.Studio,
      },
    ],
    [t]
  );

  const [selectedOption, setSelectedOption] = useState<SearchOption>(
    searchOptions[0]
  );
  const [selectedFilters, setSelectedFilters] =
    useState<SearchFiltersForVMS | null>(null);

  useEffect(() => {
    setSelectedFilters(filters || {});
  }, [filters]);

  const queryParams: SearchParams = useMemo(() => {
    const optionFilter =
      selectedOption && selectedOption.value !== 'all'
        ? { [selectedOption.prop]: selectedOption.value }
        : {};

    const rawParams = {
      needle,
      ...optionFilter,
      ...(selectedFilters || {}),
      itemsPerPage: itemsPerPage,
      page: 1, // Always start at page 1, `fetchMore` handles subsequent pages
    };

    return formatSearchParams(rawParams);
  }, [needle, selectedOption, selectedFilters]);

  const { data, loading, error, fetchMore } =
    useGetSearchResultsWithFacetsQuery({
      variables: {
        params: queryParams,
      },
      skip: !selectedFilters, // Skip the search query until filters are prepared
      notifyOnNetworkStatusChange: true,
    });

  const handleSelectOption = (item: SearchOption) => {
    setSelectedOption(item);
  };

  const items: Teaser[] = data?.searchWithFacets?.items ?? [];
  const facets: Facet[] = data?.searchWithFacets?.facets ?? [];
  const hasMore: boolean = data?.searchWithFacets?.hasMore ?? false;

  const [categoryCounts, setCategoryCounts] = useState<{
    [key: string]: number;
  }>({});

  useEffect(() => {
    if (loading) return;

    const newCounts: { [key: string]: number } = {};
    const mediaTypeCountFacet = facets.find((f) => f.name === 'mediaTypeCount');

    if (
      mediaTypeCountFacet &&
      mediaTypeCountFacet.__typename === 'CountFacet' &&
      mediaTypeCountFacet?.values
    ) {
      mediaTypeCountFacet.values.forEach((facetValue) => {
        const key = facetValue.slug
          ? String(facetValue.slug)
          : String(facetValue.value);
        newCounts[key] = facetValue.count || 0;
      });
    }

    setCategoryCounts(newCounts);
  }, [facets, loading]);

  const augmentedSearchOptions = useMemo(() => {
    const totalCount = Object.values(categoryCounts).reduce(
      (sum, count) => sum + count,
      0
    );

    return searchOptions.map((option) => {
      let count = 0;

      // The 'All' tab. It counts the sum of all others.
      if (option.value === 'all') {
        count = totalCount;
      } else if (option.prop === 'tags' && Array.isArray(option.value)) {
        // The special '360 Videos' tab, identified by its `prop`.
        // Assumes the value is an array like ['360'] and the key in counts is '360'
        const key = String(option.value[0]);
        count = categoryCounts[key] || 0;
      } else if (option.prop === 'contentType') {
        // Standard tabs ('Video', 'Series', etc.)
        const key = String(option.value).toLowerCase();
        count = categoryCounts[key] || 0;
      }
      return { ...option, count };
    });
  }, [searchOptions, categoryCounts]);

  const loadMore = () => {
    if (!hasMore || loading) return;

    fetchMore({
      variables: {
        params: {
          ...queryParams,
          page: Math.floor(items.length / itemsPerPage) + 1,
        },
      },
      updateQuery: (prevResult, { fetchMoreResult }) => {
        if (!fetchMoreResult?.searchWithFacets?.items) return prevResult;
        return {
          ...prevResult,
          searchWithFacets: {
            ...prevResult.searchWithFacets,
            items: [
              ...(prevResult.searchWithFacets.items ?? []),
              ...(fetchMoreResult.searchWithFacets.items ?? []),
            ],
            facets: fetchMoreResult.searchWithFacets.facets,
            hasMore: fetchMoreResult.searchWithFacets.hasMore,
          },
        };
      },
    });
  };

  return (
    <div>
      <Facets loading={loading || selectedFilters === null} facets={facets} />
      <PageableGrid
        options={augmentedSearchOptions}
        items={items}
        loading={loading || selectedFilters === null}
        selectedOption={selectedOption}
        hasMore={hasMore}
        error={error}
        onLoadMore={loadMore}
        onSelectOption={handleSelectOption}
      />
    </div>
  );
};

export default SearchGridBlock;
